import styles from './what-to-expect.module.scss';
import Heading from 'src/components/Heading/Heading';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { AdvancedAccordion } from '@get-mosh/msh-shared';

type WhatToExpectProps = {
    className?: string;
    accordion: {
        title: string;
        subtitle: string;
        content: React.ReactNode;
        icon: IconProps;
        children?: React.ReactNode;
    }[];
    columns: {
        icon: IconProps;
        period: string;
        stage: string;
        offset?: string;
        info?: [
            {
                text: string;
            },
        ];
        children?: React.ReactNode;
    }[];
    disclaimer?: string;
};

type IconProps = {
    src: string;
    width: number;
    height: number;
};

const defaultProps = {
    accordion: [
        {
            title: '0 to 3 months',
            subtitle: 'Setting you up for success',
            content:
                "<ul><li>Work with your doctor to develop your weight loss program. Make sure you're happy with the treatment you're on.</li><li>The treatment should begin to work, and you should expect to lose up to 5% of your body weight after completing 12 weeks of treatment.<sup>*</sup></li></ul>",
            icon: {
                src: '/images/icons/doctor-happy.webp',
                width: 80,
                height: 64,
            },
        },
        {
            title: '3 to 6 months',
            subtitle: 'See continued results',
            content:
                "<ul><li>Treatment should continue to do its work and your weight loss should increase or be maintained during this time.</li><li>Continue to work with your team to make sure you're staying on track.</li><li>See improvements in sleep, energy and physical health.</li></ul>",
            icon: { src: '/images/icons/calendar.webp', width: 80, height: 64 },
        },
        {
            title: '6 to 12 months',
            subtitle: 'Reap the rewards',
            content:
                '<ul><li>You could expect to lose even more weight. Some people achieve significant weight loss of more than 10%, with others losing 20% or more.<sup>*</sup></li><li>Treatment will keep you on track to your goals.</li><li>See significant improvements in health across key biometric markers including heart health, blood pressure, asthma.</li></ul>',
            icon: { src: '/images/icons/scale.svg', width: 80, height: 64 },
        },
    ],
    columns: [
        {
            icon: {
                src: '/images/icons/doctor-happy.webp',
                width: 80,
                height: 64,
            },
            period: '0 to 3 months',
            stage: 'Setting you up for success',
            info: [
                {
                    text: "Work with your doctor to develop your weight loss program. Make sure you're happy with the treatment you're on.",
                },
                {
                    text: 'The treatment should begin to work, and you should expect to lose up to 5% of your body weight after completing 12 weeks of treatment*',
                },
            ],
        },
        {
            icon: { src: '/images/icons/calendar.webp', width: 80, height: 64 },
            period: '3 to 6 months',
            offset: '0px',
            stage: 'See continued results',
            info: [
                {
                    text: 'Treatment should continue to do its work and your weight loss should increase or be maintained during this time.',
                },
                {
                    text: "Continue to work with your health team to ensure you're forming lasting habits and staying on track for longer term results.",
                },
                {
                    text: 'Your confidence, sleep, energy levels and general physical health will be improving.',
                },
            ],
        },
        {
            icon: { src: '/images/icons/scale.svg', width: 80, height: 64 },
            period: '6 to 12 months',
            stage: 'Reap the rewards',
            info: [
                {
                    text: 'You could expect to lose even more weight. Some people achieve significant weight loss of more than 10%, with others losing 20% or more.*',
                },
                {
                    text: 'Good habits become second nature as your treatment keeps you on track to hit your health goals.',
                },
                {
                    text: "You're seeing significant improvements in health across key biometric markers like your heart health, blood pressure, blood sugar and any asthma.",
                },
            ],
        },
    ],
    disclaimer:
        'Individual results may vary. Always consult a qualified health professional to determine if a treatment plan is right for you.',
};

type ProgressBarProps = {
    index: number;
    alt: string;
    offset?: string;
};

const ProgressBar = (column: ProgressBarProps) => {
    let ProgressImageSrc = '';
    switch (column.index) {
        case 0:
            ProgressImageSrc = '/images/elements/progress-left.svg';
            break;
        case 1:
            ProgressImageSrc = '/images/elements/progress-middle.svg';
            break;
        case 2:
            ProgressImageSrc = '/images/elements/progress-right.svg';
            break;
        default:
            ProgressImageSrc = '/images/elements/progress-middle.svg';
            break;
    }
    const offsetStyle = { marginTop: column.offset };
    return (
        <div
            className={classNames(styles['progress-stage-bar'])}
            data-progress-index={column.index}
            style={offsetStyle}
        >
            <Image
                src={ProgressImageSrc}
                alt={`What to expect - ${column.alt}`}
                width={103}
                height={34}
            />
        </div>
    );
};

const WhatToExpect = ({
    accordion,
    columns,
    disclaimer,
}: WhatToExpectProps) => (
    <div className={classNames(styles['what-to-expect'], 'what-to-expect')}>
        <div className={`mx-auto sm:max-w-md lg:hidden`}>
            <AdvancedAccordion items={accordion} />
        </div>
        <div className="mx-auto hidden xl:container lg:block">
            <div className="flex">
                {columns.map((item, i) => (
                    <article className={styles['column']} key={i}>
                        <header>
                            <Image
                                src={item.icon.src}
                                width={item.icon.width}
                                height={item.icon.height}
                                alt={item.stage}
                            />
                            <Heading>{item.period}</Heading>
                            <p className="pb-4">{item.stage}</p>
                            <ProgressBar
                                index={i}
                                alt={item.stage}
                                offset={item.offset}
                            />
                        </header>
                        <section className="px-8">
                            {item.info && item.info.length && (
                                <ul>
                                    {item.info.map((listItem, index) => {
                                        return (
                                            <li
                                                className={`${styles['list-with-chevron']}`}
                                                key={index}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faChevronRight}
                                                />
                                                {listItem.text}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            {item.children && item.children}
                        </section>
                    </article>
                ))}
            </div>
        </div>
        <small className={classNames('block pt-4', styles['disclaimer'])}>
            {disclaimer}
        </small>
    </div>
);

WhatToExpect.defaultProps = defaultProps;

export default WhatToExpect;
